/** Say My Name's existing wordmark and ORIGIN navigation. The phase readout now
 * lives in the agent panel; its former footprint remains reserved so ORIGIN and
 * the header's responsive wrapping stay exactly where they were. */
import { cn } from '@/app/lib/utils';
import type { FC } from 'react';
import { observer } from 'mobx-react-lite';
import { defineMessages, useIntl } from 'react-intl';
import type { AgentHeaderProps } from '@/app/lib/stage/StageHeader.tsx';
import { PhaseHeaderSpace } from './PhaseIndicator.tsx';
import { closeOriginPage, isOriginOpen, openOriginPage } from './origin-page.ts';
import { OriginPage } from './OriginPage.tsx';

const messages = defineMessages({
  descriptor: {
    id: 'sayMyName.header.descriptor',
    defaultMessage: 'NAMING AGENT',
    description: 'Small descriptor shown beside the SAY MY NAME wordmark',
  },
  origin: {
    id: 'sayMyName.nav.origin',
    defaultMessage: 'ORIGIN',
    description: 'Header navigation item opening the story page about why the product was built',
  },
  home: {
    id: 'sayMyName.nav.home',
    defaultMessage: 'Home',
    description: 'Accessible name for the wordmark, which returns to the naming experience',
  },
});

const NAV_INTERACTIVE =
  'cursor-pointer touch-manipulation focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-foreground/60';
// ORIGIN keeps its original treatment, including its own active underline.
const NAV_LABEL =
  'border-b pb-0.5 text-[9px] font-medium leading-none tracking-caps transition-colors duration-300 sm:text-[10px]';
const NAV_ACTIVE = 'border-foreground text-foreground';
const NAV_IDLE = 'border-transparent text-muted-foreground-subtle';

const OriginNav: FC = observer(() => {
  const intl = useIntl();
  const originOpen = isOriginOpen();
  return (
    <button type="button" aria-current={originOpen ? 'page' : undefined} onClick={openOriginPage}
      className={cn(NAV_LABEL, NAV_INTERACTIVE, 'ml-auto mr-2 flex-none sm:mr-5 lg:mr-0',
        originOpen ? NAV_ACTIVE : cn(NAV_IDLE, 'hover:text-foreground'))}>
      {intl.formatMessage(messages.origin)}
    </button>
  );
});

export const StudioHeader: FC<AgentHeaderProps> = observer(({ brandName }) => {
  const intl = useIntl();
  return (
    <>
      <header className="flex-none border-b border-panel-line bg-background/90 px-5 pt-[env(safe-area-inset-top)] backdrop-blur-sm md:px-20">
        <div className="mx-auto flex w-full max-w-container-content flex-wrap items-baseline gap-x-3 gap-y-2 py-4 sm:flex-nowrap sm:gap-x-4">
          <div className="flex flex-col items-start gap-1.5 sm:flex-row sm:items-baseline sm:gap-4">
            <button type="button" onClick={closeOriginPage}
              aria-label={intl.formatMessage(messages.home)} title={intl.formatMessage(messages.home)}
              className={cn('font-wordmark text-2xl font-normal leading-none tracking-normal text-foreground transition-opacity duration-200 hover:opacity-80 sm:text-[1.75rem]', NAV_INTERACTIVE)}
              translate="no">
              {brandName}
            </button>
            <span className="text-[10px] font-medium leading-none tracking-caps text-muted-foreground">
              {intl.formatMessage(messages.descriptor)}
            </span>
          </div>
          <OriginNav />
          <PhaseHeaderSpace />
        </div>
      </header>
      {isOriginOpen() ? <OriginPage /> : null}
    </>
  );
});
